import CreateButton from "../components/CreateButton"
import { useCallback, useEffect, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { ReactSortable } from "react-sortablejs"
import api from "../api"
import { ReactComponent as ArchiveIcon } from "../assets/images/icons/ic-archive.svg"
import { ReactComponent as CopyIcon } from "../assets/images/icons/ic-copy.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as ReorderIcon } from "../assets/images/icons/ic-reorder.svg"
import { ButtonStatus, CourseCategories, CourseStatuses, CourseType, CourseTypes } from "../common/constants"
import { Each } from "../common/Each"
import StatusBadge from "../components/StatusBadge"
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Select from "../components/Select"
import Table from "../components/Table"
import styles from "./ShopCourses.module.css"
import TextInput from "../components/TextInput"

const ARCHIVED_CATEGORY_VALUE = '__archived__'

const ShopCourses = () => {

  const navigate = useNavigate()
  const { t } = useTranslation()
  const [courses, setCourses] = useState(null)
  const [filterCourses, setFilterCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState({ open: false, title: '', text: '' })
  const [filters, setFilters] = useState({ type: null, category: null, status: null })
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [ordering, setOrdering] = useState(false)
  const [orderedCourses, setOrderedCourses] = useState([])
  const [groupedCourses, setGroupedCourses] = useState({})
  const [savingOrder, setSavingOrder] = useState(false)
  const [selected] = useState(0);
  const [q, setQ] = useState('')
  const categoryOptions = [...CourseCategories, { id: 999, name: 'Archiviati', value: ARCHIVED_CATEGORY_VALUE }]

  useEffect(() => {
    const getCourses = async () => {
      setLoading(true)
      try {
        let courses = await api.get("/admin/courses?complete=false")
        //creo dei gruppi sulla base della categoria
        const groupsCategory = {}
        courses.filter(course => course.status === 'public').forEach(course => {
          if (!groupsCategory[course.category]) {
            groupsCategory[course.category] = []
          }
          groupsCategory[course.category].push(course)
          //ordino i corsi all'interno della categoria
          groupsCategory[course.category] = groupsCategory[course.category].sort((a, b) => { return a.position - b.position })
        })
        setGroupedCourses(groupsCategory)
        setOrderedCourses(courses.sort((a, b) => { return a.position - b.position }))
        setCourses(courses.sort((a, b) => { return a.name.localeCompare(b.name) }))
        setFilterCourses(courses.sort((a, b) => { return a.name.localeCompare(b.name) }))
      }
      catch (e) {
        console.error(e)
      }
      setLoading(false)
    }

    getCourses()
  }, [])

  const getStatusColor = (status) => {
    switch (status) {
      case 'public':
        return 'var(--sf-green)'
      case 'draft':
        return 'var(--secondary)'
      case 'hidden':
        return 'var(--sf-red)'
      default:
        return 'var(--sf-grey)'
    }
  }

  const duplicate = async (courseId, duplicateAll = false) => {
    try {
      let course = await api.get(`/admin/courses/${courseId}/duplicate?duplicate_all=${duplicateAll}`)
      setCourses([...courses, course])
      setFilters({ type: null, category: null })
      setFilterCourses([...courses, course])
    }
    catch (e) {
      console.error(e)
    }
  }

  const deleteCourse = async (courseId) => {
    try {
      await api.delete(`/admin/courses/${courseId}`)
      setCourses(courses.filter(c => c.id !== courseId))
      setFilterCourses(filterCourses.filter(c => c.id !== courseId))
    }
    catch (e) {
      console.error(e)
    }
  }

  const setCourseArchived = async (courseId, archived) => {
    try {
      await api.put(`/admin/courses/${courseId}/archive?archived=${archived}`)
      setCourses((prev) => (prev || []).map((course) => {
        if (course.id !== courseId) return course
        return { ...course, archived }
      }))
    }
    catch (e) {
      console.error(e)
    }
  }

  const getCourseActions = (course) => {
    if (!course) return []
    const actions = [
      {
        label: "Duplica",
        icon: CopyIcon,
        onClick: () => {
          setAlert({
            open: true,
            title: `Duplicare ${course.name}?`,
            text: 'Scegli se vuoi duplicare solo la parte commerciale del corso (visibile in shop) oppure anche la parte didattica con edizioni e moduli.',
            actions: [
              {
                label: 'SOLO CORSO',
                onClick: async () => {
                  await duplicate(course.id, false)
                  setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                  })
                }
              },
              {
                label: 'TUTTO',
                onClick: async () => {
                  await duplicate(course.id, true)
                  setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                  })
                }
              }
            ]
          })
        }
      },
      {
        label: course.archived ? "Rimuovi dall'archivio" : "Archivia",
        icon: ArchiveIcon,
        onClick: () => {
          setAlert({
            open: true,
            title: course.archived ? `Rimuovere ${course.name} dall'archivio?` : `Archiviare ${course.name}?`,
            text: course.archived
              ? 'Il corso tornerà visibile nella lista principale.'
              : 'Il corso sarà spostato nella categoria Archiviati.',
            actions: [
              {
                label: course.archived ? 'RIPRISTINA' : 'ARCHIVIA',
                onClick: async () => {
                  await setCourseArchived(course.id, !course.archived)
                  setAlert((prev) => {
                    prev.open = false
                    return { ...prev }
                  })
                }
              }
            ]
          })
        }
      }
    ]
    if (course.status === 'draft') {
      actions.push(
        {
          label: "Elimina",
          icon: DeleteIcon,
          onClick: () => {
            setAlert({
              open: true,
              title: `Eliminare ${course.name}?`,
              text: 'L\'operazione non potrà essere annullata',
              actions: [
                {
                  label: 'ELIMINA',
                  onClick: async () => {
                    await deleteCourse(course.id)
                    setAlert((prev) => {
                      prev.open = false
                      return { ...prev }
                    })
                  },
                  color: 'var(--sf-red)'
                }
              ]
            })
          },
          color: 'var(--sf-red)'
        }
      )
    }
    return actions
  }

  // Ordinamento personalizzato: prima draft, poi public, poi hidden; all'interno ordine alfabetico per nome
  function sortCourses(list) {
    const statusOrder = { draft: 0, public: 1, hidden: 2 }
    return [...list].sort((a, b) => {
      const sa = Object.prototype.hasOwnProperty.call(statusOrder, a.status) ? statusOrder[a.status] : 999
      const sb = Object.prototype.hasOwnProperty.call(statusOrder, b.status) ? statusOrder[b.status] : 999
      if (sa !== sb) return sa - sb
      return a.name.localeCompare(b.name)
    })
  }

  const applyFilters = useCallback(() => {
    if (!courses) return
    if (q.trim().length === 0) {
      let base = courses
      switch (selected) {
        case 1:
          base = courses.filter(c => c.type === CourseType.Masterclass)
          break
        case 2:
          base = courses.filter(c => c.type === CourseType.Professional)
          break
        case 3:
          base = courses.filter(c => c.type === CourseType.Bootcamp)
          break
        default:
          base = courses
      }
      const filtered = base.filter(c => {
        const archivedMatch = filters.category === ARCHIVED_CATEGORY_VALUE ? c.archived === true : c.archived !== true
        const categoryMatch = filters.category && filters.category !== ARCHIVED_CATEGORY_VALUE ? c.category === filters.category : true
        return archivedMatch &&
          categoryMatch &&
          (filters.type ? c.type === filters.type : true) &&
          (filters.status ? c.status === filters.status : true)
      })
      setFilterCourses(sortCourses(filtered))
    } else {
      const filtered = courses.filter(c => {
        const archivedMatch = filters.category === ARCHIVED_CATEGORY_VALUE ? c.archived === true : c.archived !== true
        const categoryMatch = filters.category && filters.category !== ARCHIVED_CATEGORY_VALUE ? c.category === filters.category : true
        return c.name.toLowerCase().includes(q.toLowerCase()) &&
          archivedMatch &&
          categoryMatch &&
          (filters.type ? c.type === filters.type : true) &&
          (filters.status ? c.status === filters.status : true)
      })
      setFilterCourses(sortCourses(filtered))
    }
  }, [courses, filters, q, selected])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>Shop - Corsi</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <header className={styles.header}>
              <div>
                <p className={styles.eyebrow}>Shop</p>
                <h1>{t('courses.title')}</h1>
                <p>Gestisci il catalogo, la visibilità e l'ordine dei corsi nello shop.</p>
              </div>
              <div className={styles.headerActions}>
                <Button appearance="subtle" requiresEdit disabled={loading} onClick={() => setOrdering(true)}>
                  <ReorderIcon aria-hidden="true" /> Riordina corsi
                </Button>
                <CreateButton onClick={() => navigate('/shop_courses/create/editor')}>
                   Aggiungi corso
                </CreateButton>
              </div>
            </header>

            <Card appearance="modern" integratedTable title="Elenco corsi" actions={<span className={styles.resultCount} role="status">{loading ? 'Caricamento…' : `${filterCourses.length} corsi`}</span>}>
              <div className={styles.cardInner}>
                <div className={styles.search}>
                <TextInput
                  appearance="modern"
                  value={q}
                  onKeyUp={(value) => {
                    setQ(value);
                  }}
                  type="search"
                  placeholder={t("search")} />
                <button
                  type="button"
                  className={styles.filterButton}
                  aria-expanded={filtersOpen}
                  aria-controls="shop-course-filters"
                  onClick={() => setFiltersOpen(open => !open)}>
                  <FilterIcon aria-hidden="true" />
                  Filtri{Object.values(filters).filter(Boolean).length > 0 ? ` (${Object.values(filters).filter(Boolean).length})` : ''}
                </button>
                </div>
                <div id="shop-course-filters" className={styles.filters} hidden={!filtersOpen}>
                  <div className={styles.filterField}>
                    <span>Tipo</span>
                  <Select appearance="modern" portal placeholder={"Tutti i tipi"} nullLabel="Tutti i tipi" nullable options={CourseTypes} selected={CourseTypes.find(o => o.value === filters.type)} onSelect={(item) => {
                    setFilters((prev) => {
                      prev.type = item?.value ?? null
                      return { ...prev }
                    })
                  }} />
                  </div>
                  <div className={styles.filterField}>
                    <span>Categoria</span>
                  <Select appearance="modern" portal placeholder={"Tutte le categorie"} nullLabel="Tutte le categorie" nullable options={categoryOptions} selected={categoryOptions.find(o => o.value === filters.category)} onSelect={(item) => {
                    setFilters((prev) => {
                      prev.category = item?.value ?? null
                      return { ...prev }
                    })
                  }} />
                  </div>
                  <div className={styles.filterField}>
                    <span>Stato</span>
                  <Select appearance="modern" portal placeholder={"Tutti gli stati"} nullLabel="Tutti gli stati" nullable options={CourseStatuses} selected={CourseStatuses.find(o => o.value === filters.status)} onSelect={(item) => {
                    setFilters((prev) => {
                      prev.status = item?.value ?? null
                      return { ...prev }
                    })
                  }} />
                  </div>
                </div>

                <div className={styles.courses}>
                  <Table
                    appearance="minimal"
                    style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
                    loading={loading}
                    loadingN={20}
                    columns={[
                      { key: 'id', label: 'ID', type: 'number' },
                      { key: 'thumbnail', label: 'COPERTINA', type: 'image' },
                      { key: 'name', label: 'NOME', type: 'string', wrap: true },
                      { key: 'type', label: 'TIPO', type: 'string' },
                      { key: 'status', label: 'STATO', type: 'custom' },
                      { key: 'actions', label: 'AZIONI', type: 'custom' }
                    ]}
                    elements={
                      filterCourses.map(c => ({
                        id: c.id,
                        name: c.name,
                        position: c.position,
                        description: c.description,
                        thumbnail: <img src={c.thumbnail} alt="" className={styles.thumbnail} />,
                        status: { value: c.status, type: 'string', component: <div className={styles.statusBadge}><StatusBadge appearance="soft" label={t(`status.${c.status}`)} color={getStatusColor(c.status)} /></div> },
                        type: t(`courses.types.${c.type}`),
                        actions: <MeatBallsMenu style={{ padding: '0.5rem' }} actions={getCourseActions(c)} />,
                        href: `/shop_courses/${c.id}/editor`
                      })).sort((a, b) => a.name.localeCompare(b.name))
                    }
                    defaultSort={{ key: 'position', direction: 'asc' }}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Dialog appearance="modern" open={ordering} title={"Riordina Corsi"} onClose={() => { setOrdering(false) }}
        action={
          <Button
            status={savingOrder ? ButtonStatus.Loading : null}
            disabled={savingOrder}
            onClick={async () => {
              setSavingOrder(true)
              try {
                await api.put('/admin/courses/reorder', { ordered_ids: orderedCourses.map(c => c.id) })
                setOrdering(false)
              }
              catch (e) {
                console.error(e)
              }
              setSavingOrder(false)
            }}>SALVA</Button>
        }
      >
        <div className={styles.dialogContent}>
          <div className={styles.dialogDescription}>Trascina i corsi per riordinarli. Al termine clicca su "Salva" per confermare le modifiche.</div>
          <Each of={Object.entries(groupedCourses)} render={([category, courses]) => {
            return (
              <div className={styles.categoryGroup}>
                <div className={styles.categoryTitle}>{CourseCategories.find(c => c.value === category)?.label || category}</div>
                <ReactSortable list={courses} setList={(newList) => {
                  setGroupedCourses((prev) => {
                    // Aggiorno l'attributo position in base al nuovo ordine
                    newList.forEach((course, index) => {
                      course.position = index + 1
                    })
                    prev[category] = newList
                    // Aggiorno anche orderedCourses per mantenere la coerenza
                    const newOrderedCourses = []
                    Object.values(prev).forEach(courseList => {
                      courseList.forEach(course => {
                        newOrderedCourses.push(course)
                      })
                    })
                    setOrderedCourses(newOrderedCourses)
                    return { ...prev }
                  })
                }}>
                  <Each of={courses} render={(course) => {
                    return (
                      <div className={styles.sortableCourse}><ReorderIcon className={styles.reorderIcon} /> <span><div>{course.name}</div> <div className={styles.courseType}>{t(`courses.types.${course.type}`)}</div></span></div>
                    )
                  }} />
                </ReactSortable>
              </div>
            )
          }} />
        </div>
      </Dialog>
      <AlertDialog open={alert.open} title={alert.title} text={alert.text} actions={alert.actions} onClose={() => {
        setAlert((prev) => {
          prev.open = false
          return { ...prev }
        })
      }} />
    </HeaderFooterLayout>
  )

}

export default ShopCourses
